import { useLazyQuery, useQuery, useReactiveVar } from '@apollo/client'
import { useLoading, useServerError } from 'shared/hooks'
import { GET_PROJECT, GET_USER } from 'modulesCommon/graphql/queries'
import { activeArtistVar, activeProjectVar, currentUserVar, labelCodeIdVar, ownerVar } from 'modulesCommon/store'
import { useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router'
import { getUnits } from 'shared/utils/labels'
import { IArtist, IProject } from 'shared/interfaces'
import { useZonesList } from 'modulesCommon/serviceHooks'
import { useURLBuilder } from 'shared/hooks'

const useArtistInfo = (projectId?: string, preventArtistFlush = false) => {
  const { getProjectPageUrl } = useURLBuilder()

  const navigate = useNavigate()
  const { zoneList, zoneLoading } = useZonesList()
  const activeArtist = useReactiveVar<IArtist>(activeArtistVar)
  const activeProject = useReactiveVar<IProject>(activeProjectVar)
  const currentLabel = useReactiveVar(labelCodeIdVar)
  const owner = useReactiveVar(ownerVar)
  const currentUser = useReactiveVar(currentUserVar)

  const units = useMemo(() => getUnits(zoneList, currentLabel), [zoneList, currentLabel])
  const { data: campaignData, error: campaignError, loading: campaignLoading } = useQuery<{ getCampaign: IProject }, {
    id: string
  }>(GET_PROJECT, {
    skip: !projectId,
    fetchPolicy: 'network-only',
    variables: {
      id: projectId ?? ''
    }
  })

  const [getOwnerData, { data: userData, loading: ownerLoading, error: ownerError }] = useLazyQuery(GET_USER)

  const isUserLabel = useMemo(() => `${currentUser?.label_id}` === currentLabel, [currentUser, currentLabel])

  useEffect(() => {
    if (!campaignData?.getCampaign) return
    const project = campaignData?.getCampaign as IProject
    const isGlobalhubPage = window.location.pathname.includes('globalhub')
    const isGlobalProject = project?.isGlobal
    const isPreviewPage = window.location.pathname.includes('preview')
    if (!isPreviewPage) {
      if (isGlobalProject && !isGlobalhubPage) {
        console.log('redirect to globalhub')
        navigate(getProjectPageUrl(project.id, true))
      } else if (!isGlobalProject && isGlobalhubPage) {
        console.log('redirect to sbx')
        navigate(getProjectPageUrl(project.id, false))
      }
    }
    // if (project.label_id) setZone(project.label_id.toString())
  }, [campaignData?.getCampaign, getProjectPageUrl, navigate, projectId])

  useEffect(() => {
    if (campaignData) {
      if (!campaignData.getCampaign.id) {
        navigate('/feed')
      }

      const campaign = campaignData.getCampaign

      activeArtistVar(campaignData.getCampaign?.artist)
      activeProjectVar(campaign)
    }
  }, [campaignData, navigate])

  useEffect(() => {
    if (activeProject?.owner_id) {
      getOwnerData({
        variables: {
          id: +activeProject?.owner_id
        }
      })
    }
  }, [activeProject, getOwnerData])

  useEffect(() => {
    if (userData) ownerVar(userData?.getUser)
  }, [userData])

  const isCampaignOwner = useMemo(() => owner?.id === currentUser?.id, [currentUser, owner])

  useLoading({ isLoading: campaignLoading || ownerLoading || zoneLoading })

  useServerError([campaignError, ownerError])

  useEffect(
    () => () => {
      if (!preventArtistFlush) {
        activeArtistVar({})
        activeProjectVar({})
      }
    },
    [preventArtistFlush]
  )

  return { owner, units, activeArtist, activeProject, isUserLabel, isCampaignOwner, campaignLoading }
}

export default useArtistInfo
